import { useEffect, useRef, useState } from "react";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import { useScrollReveal } from "../components/useScrollReveal";
import { useSEO } from "../components/useSEO";
import "./Platform.css";

const RATING_TARGET = 77;

function getScoreColor(score) {
  if (score >= 60) return "var(--green)";
  if (score >= 30) return "var(--amber)";
  return "var(--red)";
}

export default function Platform() {
  useScrollReveal();
  useSEO(
    "Modus PDM Platform | AI Provider Search, Scoring & Verification",
    "Modus layers AI scoring, red-flag detection, call verification, and real-time referral checks on top of provider search, so every result is one you can trust."
  );

  // Same real-tier-synced ring treatment as the Home page's dashboard
  // mockup — draws itself and shifts color as it counts up, rather
  // than appearing instantly at its final value.
  const [ratingScore, setRatingScore] = useState(0);
  const heroSectionRef = useRef(null);

  // Background depth parallax: the hero's existing decorative glow
  // (previously a static ::before, now a real element so it can be
  // scroll-driven) moves at a fraction of scroll speed relative to the
  // foreground — classic parallax depth, kept subtle and confined to
  // this one hero section rather than applied everywhere.
  useEffect(() => {
    const glow = document.getElementById("platHeroGlow");
    const section = heroSectionRef.current;
    if (!glow || !section) return;
    if (!window.matchMedia("(prefers-reduced-motion: no-preference)").matches) return;

    const handleScroll = () => {
      const rect = section.getBoundingClientRect();
      // Only move while the section is anywhere near the viewport —
      // avoids running this calculation for a section scrolled far away.
      if (rect.bottom < -200 || rect.top > window.innerHeight + 200) return;
      glow.style.transform = `translateY(${rect.top * 0.15}px)`;
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const reducedMotion = !window.matchMedia("(prefers-reduced-motion: no-preference)").matches;
    if (reducedMotion) {
      setRatingScore(RATING_TARGET);
      return;
    }
    const dur = 1400;
    const start = performance.now();
    let rafId;
    function tick(now) {
      const p = Math.min((now - start) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setRatingScore(RATING_TARGET * eased);
      if (p < 1) rafId = requestAnimationFrame(tick);
    }
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, []);

  return (
    <>
      <SiteHeader active="platform" />


<section className="plat-hero" style={{paddingBottom: "60px"}} ref={heroSectionRef}>
  <div className="hero-glow-blob" id="platHeroGlow" />
  <img src="/patented-badge.png" alt="Patented, protected" className="patented-badge" />
  <div className="wrap plat-hero-grid">
    <div className="plat-hero-copy">
      <p className="kicker">The Platform</p>
      <h1>AI capabilities, <span className="accent-teal">layered on top</span> of your provider search.</h1>
      <p>Type a name. Modus layers scoring, red-flag detection, call verification, and more on top of that single search, so what comes back isn't just a match, it's one you can actually trust.</p>
      <div className="modjump">
        <a href="#scoring">01 Provider Rating</a>
        <a href="#red-flag">02 AI Red Flag</a>
        <a href="#call-verification">03 Call Verification</a>
        <a href="#team-notes">04 Team Notes</a>
        <a href="#agentic-layer">05 Agentic Layer</a>
        <a href="#integrations">06 Integrations</a>
        <a href="#npd">07 NPD Integration</a>
      </div>
    </div>
    <div className="plat-hero-visual">
      <div className="phv-frame">
        <div className="phv-search">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round"><circle cx="11" cy="11" r="7"></circle><line x1="21" y1="21" x2="16.6" y2="16.6"></line></svg>
          <span>Dr. Elena Ruiz, Ortho...</span>
          <span className="phv-live"><span className="dot"></span>Live</span>
        </div>
        <div className="phv-stack">
          <div className="phv-layer" style={{"--i": "0"}}><span className="phv-ico"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="20" x2="12" y2="10"/><line x1="18" y1="20" x2="18" y2="4"/><line x1="6" y1="20" x2="6" y2="16"/></svg></span><div><div className="phv-t">Provider Rating</div><div className="phv-s">94 / 100, top decile</div></div><span className="phv-check"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg></span></div>
          <div className="phv-layer" style={{"--i": "1"}}><span className="phv-ico"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="3"/></svg></span><div><div className="phv-t">AI Red Flag</div><div className="phv-s">Clear, checked today</div></div><span className="phv-check"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg></span></div>
          <div className="phv-layer" style={{"--i": "2"}}><span className="phv-ico"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg></span><div><div className="phv-t">Call Verification</div><div className="phv-s">Verified this morning</div></div><span className="phv-check"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg></span></div>
          <div className="phv-layer" style={{"--i": "3"}}><span className="phv-ico"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z"/></svg></span><div><div className="phv-t">Team Notes</div><div className="phv-s">3 notes from your team</div></div><span className="phv-check"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg></span></div>
        </div>
        <div className="phv-more">+ Agentic Layer · Integrations</div>
      </div>
    </div>
  </div>
</section>


<section>
  <div className="wrap">
    <div className="section-head reveal wide">
      <p className="kicker">How your team uses it</p>
      <h2 className="section-title">Find the right doctor the first time. <span className="accent-teal">Not just the fastest yes.</span></h2>
      <p className="section-lead">Once your network is augmented, your team stops calling around and starts searching. Claims teams save an average of <strong className="accent-teal">4 hours a week</strong> using Sophia AI, and every referral goes to a doctor backed by data, not just whoever picked up the phone first or sends the best donuts to your office.</p>
    </div>

    <div className="layer-flow reveal-stagger">
      <div className="layer-step">
        <div className="layer-icon"><svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg></div>
        <div className="layer-step-label">Step 1</div>
        <h3>Search your way</h3>
        <p>By location, name, or body part operated on. Or just ask Sophia AI what kind of doctor you're looking for.</p>
        <div className="layer-mini mini-channels-list">
          <div className="mini-channel-row"><span className="mini-channel"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 21s-7-6.2-7-11a7 7 0 0 1 14 0c0 4.8-7 11-7 11Z"/><circle cx="12" cy="10" r="2.4"/></svg></span>Location</div>
          <div className="mini-channel-row"><span className="mini-channel"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg></span>Provider name</div>
          <div className="mini-channel-row"><span className="mini-channel"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg></span>Body part</div>
          <div className="mini-channel-row"><span className="mini-channel"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-3.8 7 8.5 8.5 0 0 1-9.8.9L3 21l1.6-4.4a8.5 8.5 0 0 1 .9-9.8 8.38 8.38 0 0 1 7-3.8h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg></span>Ask Sophia AI</div>
        </div>
      </div>
      <div className="layer-connector"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="13 6 19 12 13 18"/></svg><span className="flow-dot"></span></div>
      <div className="layer-step">
        <div className="layer-icon"><svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/><line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/><line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/><line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/><line x1="17" y1="16" x2="23" y2="16"/></svg></div>
        <div className="layer-step-label">Step 2</div>
        <h3>Ranked your way</h3>
        <p>Results sorted by what matters to your team: distance, in-network status, Modus rating, and more, configured by you.</p>
        <div className="layer-mini">
          <div className="layer-skel-row done"><div className="layer-skel-bar" style={{width: "52%"}}></div><span className="layer-skel-tag">Modus 91</span></div>
          <div className="layer-skel-row done"><div className="layer-skel-bar" style={{width: "38%"}}></div><span className="layer-skel-tag">In network</span></div>
          <div className="layer-skel-row done"><div className="layer-skel-bar" style={{width: "46%"}}></div><span className="layer-skel-tag">2.1 mi</span></div>
        </div>
      </div>
      <div className="layer-connector"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="13 6 19 12 13 18"/></svg><span className="flow-dot"></span></div>
      <div className="layer-step">
        <div className="layer-icon"><svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg></div>
        <div className="layer-step-label">Step 3</div>
        <h3>Confirm &amp; refer</h3>
        <p>Select your doctor and confirm real-time WC acceptance, contact information, and appointment availability, then send the referral letter.</p>
        <div className="layer-mini mini-score">
          <div className="mini-score-caps">
            <div className="mini-score-cap"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2 4 6v6c0 5 3.5 8.5 8 10 4.5-1.5 8-5 8-10V6l-8-4Z"/><polyline points="9 12 11.5 14.5 15.5 10"/></svg>Realtime WC acceptance</div>
            <div className="mini-score-cap"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"/></svg>Contact info confirmed</div>
            <div className="mini-score-cap"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>Appointment availability</div>
            <div className="mini-score-cap"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-10 5L2 7"/></svg>Referral letter sent</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>


<section id="scoring">
  <div className="wrap">
    <div className="mod-head reveal"><span className="mod-num">01</span><h2 className="mod-title">Provider Rating</h2></div>
    <div className="rf-top reveal">
      <div className="rf-left">
        <p className="mod-lead">Medicare-data-backed scoring, refreshed yearly and NPI-verified: built by analyzing each provider across 7-8 weighted clinical and cost parameters, then compared apples-to-apples against true peers in the same specialty and geography.</p>
        <div className="mod-copy">
          <ul>
            <li>183M historical patient records behind every score</li>
            <li>Scored across 7-8 weighted parameters: cost, complications, outcomes, and more</li>
            <li>Peer-and-geography-adjusted, never a flat national average</li>
            <li>Published numerator/denominator methodology, not a black box</li>
            <li>Validated by a $100K Financial Credibility Guarantee (The Validation Institute)</li>
          </ul>
        </div>
      </div>
      <div className="rf-right">
        <div className="mod-visual">
          <div className="mrating-card">
            <div className="mrating-head">
              <span className="mrating-label">MODUS RATING</span>
            </div>
            <div className="mrating-body">
              <div className="mrating-ring" style={{"--pct": ratingScore, "--ring-color": getScoreColor(ratingScore)}}>
                <span className="mrating-num" style={{color: getScoreColor(ratingScore)}}>{Math.round(ratingScore)}</span>
              </div>
              <div className="mrating-info">
                <div className="mrating-name">Dr. Elena Ruiz, MD</div>
                <div className="mrating-sub">Above peer average.<br /><strong>0</strong> flags in the last 12 months.</div>
              </div>
            </div>
          </div>
        </div>
        <div className="mod-quote">"The best surgeons are those who think twice and cut once; their patients are better, not worse off, afterward."</div>
      </div>
    </div>
  </div>
</section>


<section id="red-flag" style={{background: "var(--surface)"}}>
  <div className="wrap">
    <div className="mod-head reveal"><span className="mod-num">02</span><h2 className="mod-title">AI Red Flag Detection</h2></div>
    <div className="rf-top reveal">
      <div className="rf-left">
        <p className="mod-lead">Network risk doesn't wait for OIG's next update, and neither should you. An AI agent searches the open web for every provider, checking name, address, NPI, and license against federal sources (CMS, OIG, DEA) and state license boards for legal or disciplinary history, before it becomes your problem.</p>
        <div className="mod-visual compact">
          <div className="bgchk-head">
            <span className="bgchk-title">Network Scan · Full Panel</span>
            <span className="pill-sm bad">3 flagged</span>
          </div>
          <div className="netscan-row"><span className="netscan-name">Robert Klein, M.D.</span><span className="netscan-badge teal">81</span><span className="pill-sm bad">Flagged</span></div>
          <div className="netscan-row"><span className="netscan-name">Sandra Voss, MD</span><span className="netscan-badge gray">61</span><span className="pill-sm bad">Flagged</span></div>
          <div className="netscan-row"><span className="netscan-name">Thomas Reyes, M.D.</span><span className="netscan-badge gray">52</span><span className="pill-sm bad">Flagged</span></div>
        </div>
        <div className="mod-quote">"Don't wait for the OIG's next update. Find out who's already in your network."</div>
      </div>
      <div className="rf-right">
        <div className="mod-visual compact">
          <div className="bgchk-head">
            <span className="bgchk-title">Background Search</span>
            <span className="pill-sm info">Run background check</span>
          </div>
          <div className="bgchk-name">Dr. Marcus Webb</div>
          <div className="bgchk-meta">Last searched today · NPI-matched</div>
          <div className="bgchk-summary">No legal or disciplinary red flags found. Federal and state sources show no indictments, license actions, or sanctions.</div>
          <div className="bgchk-src">Sources: OIG · CMS · DEA · State Medical Board</div>
        </div>
        <ul>
          <li>Matches by name, address, NPI, and license number, not just a name</li>
          <li>Checks CMS, OIG, DEA, and every state medical board</li>
          <li>Scans your whole network on a schedule, or run it on demand any time</li>
          <li>Finds problems early, not at year-end</li>
        </ul>
      </div>
    </div>
  </div>
</section>


<section id="call-verification">
  <div className="wrap">
    <div className="mod-head reveal"><span className="mod-num">03</span><h2 className="mod-title">AI Call Verification</h2></div>
    <div className="cv-top reveal">
      <div className="cv-left">
        <p className="mod-lead">This feature runs on demand. Our AI agent, Sophia, calls the doctor's office directly to confirm workers' comp acceptance, verify contact details, and confirm appointment availability in the next few weeks.</p>
        <div className="mod-copy">
          <ul>
            <li>Sophia calls the office directly, on demand, whenever you need a fresh check</li>
            <li>Confirms workers' comp acceptance, contact details, and appointment availability</li>
            <li>Every check is logged with a timestamp you can trust</li>
            <li>No one on your team has to pick up a phone</li>
          </ul>
        </div>
      </div>
      <div className="mod-visual">
        <div className="bgchk-head">
          <span className="bgchk-title">Call Verification Log</span>
          <span className="pill-sm info">Sophia · On demand</span>
        </div>
        <div className="cv-row cv-row-head">
          <span>Provider</span><span>WC</span><span>1M Apmt</span>
        </div>
        <div className="cv-row">
          <span className="cv-name">Daniel Ortiz, M.D.</span>
          <span className="cv-wc"><span className="cv-badge">WC</span><span className="cv-date">Sep 15, 2026</span></span>
          <span className="cv-apmt"><span className="cv-check"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg></span><span className="cv-date">Sep 15, 2026</span></span>
        </div>
        <div className="cv-row">
          <span className="cv-name">Priya Nair, MD</span>
          <span className="cv-wc"><span className="cv-badge">WC</span><span className="cv-date">Sep 15, 2026</span></span>
          <span className="cv-apmt"><span className="cv-pending">Not yet</span></span>
        </div>
        <div className="cv-row">
          <span className="cv-name">Alan Brooks, M.D.</span>
          <span className="cv-wc"><span className="cv-pending">Not yet</span></span>
          <span className="cv-apmt"><span className="cv-pending">Not yet</span></span>
        </div>
      </div>
    </div>
  </div>
</section>


<section id="team-notes" style={{background: "var(--surface)"}}>
  <div className="wrap">
    <div className="mod-head reveal"><span className="mod-num">04</span><h2 className="mod-title">Team Notes &amp; Provider Collaboration</h2></div>
    <div className="rf-top reveal">
      <div className="rf-left">
        <p className="mod-lead">Care navigators can agree, disagree, or add their own notes on any provider, visible to just themselves or shared with the whole team. Nothing gets lost when someone leaves. What they learned stays with the organization.</p>
        <div className="mod-copy">
          <ul>
            <li>React with Agree, Disagree, or Definitely agree, or add a note of your own</li>
            <li>Visible to just you or shared with the whole team, your call</li>
            <li>One flagged experience helps the whole team avoid a bad fit</li>
            <li>Stays with the organization, even after the navigator who wrote it moves on</li>
            <li>Threaded directly on the provider's record, next to the score</li>
          </ul>
        </div>
        <div className="mod-visual">
          <div className="bgchk-head">
            <span className="bgchk-title">Activity Panel</span>
          </div>
          <div className="ap-row">
            <span className="ap-avatar">MT</span>
            <div className="ap-body">
              <div className="ap-name">Maria T. <span className="ap-action">agreed</span></div>
              <div className="ap-snip">"Great with anxious patients, would refer again."</div>
            </div>
            <span className="ap-time">2d</span>
          </div>
          <div className="ap-row">
            <span className="ap-avatar">JK</span>
            <div className="ap-body">
              <div className="ap-name">James K. <span className="ap-action">commented</span></div>
              <div className="ap-snip">"Outcomes have been good across 3 referrals."</div>
            </div>
            <span className="ap-time">1w</span>
          </div>
          <div className="ap-row">
            <span className="ap-avatar">RS</span>
            <div className="ap-body">
              <div className="ap-name">Rita S. <span className="ap-action">definitely agreed</span></div>
              <div className="ap-snip">"Confirmed, same experience on our end."</div>
            </div>
            <span className="ap-time">2w</span>
          </div>
        </div>
      </div>
      <div className="rf-right">
        <div className="mod-visual">
          <div className="bgchk-head">
            <span className="bgchk-title">Provider Notes</span>
          </div>
          <div className="tn-toggle">
            <span className="tn-toggle-btn active">Only me</span>
            <span className="tn-toggle-btn">For everyone</span>
          </div>
          <div className="tn-note">
            <div className="tn-note-head"><span className="tn-avatar">MT</span><span className="tn-author">Maria T. <span className="tn-role">Case Manager</span></span><span className="tn-time">2d ago</span></div>
            <div className="tn-text">"Tried this provider for the first time. He was great, and the patient had a good experience."</div>
            <div className="tn-reacts">
              <span className="tn-rbtn"><svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 14V2"/><path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22h0a3.13 3.13 0 0 1-3-3.88Z"/></svg><span>0</span></span>
              <span className="tn-rbtn active"><svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M7 10v12"/><path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2h0a3.13 3.13 0 0 1 3 3.88Z"/></svg><span>4</span></span>
              <span className="tn-rbtn filled"><svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M7 10v12"/><path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2h0a3.13 3.13 0 0 1 3 3.88Z"/></svg><span>0</span></span>
            </div>
          </div>
          <div className="tn-note">
            <div className="tn-note-head"><span className="tn-avatar">JK</span><span className="tn-author">James K. <span className="tn-role">WC Adjuster</span></span><span className="tn-time">1w ago</span></div>
            <div className="tn-text">"We've referred Dr. Zack Coleman to 3 of our members. Outcomes have been good, matching his Modus score."</div>
            <div className="tn-reacts">
              <span className="tn-rbtn"><svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 14V2"/><path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22h0a3.13 3.13 0 0 1-3-3.88Z"/></svg><span>0</span></span>
              <span className="tn-rbtn"><svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M7 10v12"/><path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2h0a3.13 3.13 0 0 1 3 3.88Z"/></svg><span>1</span></span>
              <span className="tn-rbtn filled"><svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M7 10v12"/><path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2h0a3.13 3.13 0 0 1 3 3.88Z"/></svg><span>2</span></span>
            </div>
          </div>
          <div className="tn-add">
            <div className="tn-input">Add a note on this provider...</div>
            <span className="tn-post">Post</span>
          </div>
        </div>
        <div className="mod-quote">"Availability got you a quick answer. This gets you the right one."</div>
      </div>
    </div>
  </div>
</section>


<section id="agentic-layer">
  <div className="wrap">
    <div className="mod-head reveal"><span className="mod-num">05</span><h2 className="mod-title">Agentic Layer</h2></div>
    <div className="rf-top reveal">
      <div className="rf-left">
        <p className="mod-lead">Modus is a platform other agents plug into, not just a lookup tool. Bring your own agent and pull scored, verified provider data into your downstream systems over MCP, or tell us what you need and we'll build and deploy a custom agent for your workflow in days.</p>
        <div className="mod-copy">
          <div className="agent-card"><div className="agent-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg></div><div><div className="agent-txt">Book the next available slot</div><div className="agent-sub">Scheduling agent</div></div></div>
          <div className="agent-card"><div className="agent-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg></div><div><div className="agent-txt">Auto-route this referral to the best-scored in-network match</div><div className="agent-sub">Referral automation agent</div></div></div>
          <div className="agent-card"><div className="agent-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22v-5"/><path d="M9 8V2"/><path d="M15 8V2"/><path d="M18 8v5a4 4 0 0 1-4 4h-4a4 4 0 0 1-4-4V8Z"/></svg></div><div><div className="agent-txt">Pull scored, verified provider data into your own systems</div><div className="agent-sub">MCP data access</div></div></div>
          <div className="agent-card"><div className="agent-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 3v4a1 1 0 0 0 1 1h4"/><path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2Z"/></svg></div><div><div className="agent-txt">Tell us what you need, we'll build and deploy it in days</div><div className="agent-sub">Custom agent, built for you</div></div></div>
        </div>
      </div>
      <div className="rf-right">
        <div className="mod-visual">
          <div className="bgchk-head">
            <span className="bgchk-title">Voice &amp; Text Agents</span>
            <span className="pill-sm info">Live</span>
          </div>
          <div className="va-row">
            <div className="va-wave"><span></span><span></span><span></span><span></span><span></span></div>
            <div className="va-body">
              <div className="va-label">Voice agent</div>
              <div className="va-text">"Checking availability for Dr. Ruiz, next Tuesday..."</div>
            </div>
          </div>
          <div className="va-row">
            <div className="va-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg></div>
            <div className="va-body">
              <div className="va-label">Text agent</div>
              <div className="va-text">Referral routed to the best-scored match in Austin, TX.</div>
            </div>
          </div>
        </div>
        <div className="mod-visual">
          <div className="bgchk-head">
            <span className="bgchk-title">AI Safety</span>
          </div>
          <div className="safety-row"><span className="cv-check"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg></span><span>PHI never leaves your environment</span></div>
          <div className="safety-row"><span className="cv-check"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg></span><span>Every agent action is logged and auditable</span></div>
          <div className="safety-row"><span className="cv-check"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg></span><span>Human escalation available at any step</span></div>
        </div>
      </div>
    </div>
  </div>
</section>


<section id="integrations" style={{background: "var(--surface)"}}>
  <div className="wrap">
    <div className="mod-head reveal"><span className="mod-num">06</span><h2 className="mod-title">Integrations</h2></div>
    <div className="rf-top reveal">
      <div className="rf-left">
        <p className="mod-lead">Modus is highly integrable, backed by an open API. Pull Modus data straight into your claims system, work inside the chat tools your team already has open like Microsoft Teams or Slack, no new logins, no new window, or use the Modus UI on its own if that's easier.</p>
        <div className="mod-copy">
          <div className="agent-card"><div className="agent-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22v-5"/><path d="M9 8V2"/><path d="M15 8V2"/><path d="M18 8v5a4 4 0 0 1-4 4h-4a4 4 0 0 1-4-4V8Z"/></svg></div><div><div className="agent-txt">Open API</div><div className="agent-sub">Pull scored, verified provider data into any system</div></div></div>
          <div className="agent-card"><div className="agent-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="1"/></svg></div><div><div className="agent-txt">Claims system</div><div className="agent-sub">Bring Modus data directly into your claims workflow</div></div></div>
          <div className="agent-card"><div className="agent-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg></div><div><div className="agent-txt">Microsoft Teams &amp; Slack</div><div className="agent-sub">Right inside the chat your team already has open</div></div></div>
          <div className="agent-card"><div className="agent-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg></div><div><div className="agent-txt">Modus UI</div><div className="agent-sub">Prefer a dedicated app, use Modus on its own</div></div></div>
        </div>
      </div>
      <div className="rf-right">
        <div className="mod-visual">
          <div className="bgchk-head">
            <span className="bgchk-title">Inside Microsoft Teams</span>
            <span className="pill-sm info">No new login</span>
          </div>
          <div className="chat-bubble">@Modus, what's Dr. Ruiz's score?</div>
          <div className="chat-bubble me">Dr. Elena Ruiz, MD: 94 / 100. Above peer average, 0 flags in the last 12 months.</div>
        </div>
        <div className="mod-visual">
          <div className="bgchk-head">
            <span className="bgchk-title">Your Systems</span>
          </div>
          <div className="safety-row"><span className="cv-check"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg></span><span>Claims system, data pushed automatically</span></div>
          <div className="safety-row"><span className="cv-check"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg></span><span>Open API, pull scores into anything you run</span></div>
          <div className="safety-row"><span className="cv-check"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg></span><span>Or just use the Modus UI on its own</span></div>
        </div>
      </div>
    </div>
  </div>
</section>

<section id="npd">
  <div className="wrap">
    <div className="mod-head reveal">
      <span className="mod-num">07</span>
      <h2 className="mod-title">NPD Integration</h2>
      <span className="mod-badge">Industry first</span>
    </div>
    <div className="rf-top reveal">
      <div className="rf-left">
        <p className="mod-lead">CMS's old NPPES file is a self-reported spreadsheet, refreshed monthly at best. The new CMS National Provider Directory (NPD) is a live FHIR API, pulling identity, credentials, and organization affiliations straight from CMS's own systems in near real time. Modus is the first provider lookup tool to layer it in automatically, no manual entry, no waiting for next month's file.</p>
        <div className="mod-copy">
          <ul>
            <li>Identity and credentials verified directly from CMS, not self-reported</li>
            <li>Practice locations, license status, and specialties kept current automatically</li>
            <li>Insurance and coverage shown honestly, only what CMS has actually published</li>
            <li>Hospital affiliations and practice groups shown explicitly, not inferred from billing</li>
            <li>Digital endpoints included, so systems can connect the moment they're listed</li>
          </ul>
        </div>
      </div>
      <div className="rf-right">
        <div className="mod-visual compact">
          <div className="bgchk-head">
            <span className="bgchk-title">Personal &amp; legal info</span>
            <span className="pill-sm good">Currently practicing</span>
          </div>
          <div className="bgchk-name">Dr. James Calder, MD</div>
          <div className="bgchk-meta">NPI 123456789 · Doctor of Medicine · In practice since 2005</div>
          <div className="bgchk-summary">Orthopaedic Surgery Physician, also credentialed in Adult Reconstructive Orthopaedic Surgery.</div>
          <div className="bgchk-meta">Hospital affiliations: Mercy Regional Medical Center, Lakeside Surgical Center</div>
          <div className="bgchk-meta">Practice: Advanced Orthopaedic Partners</div>
          <div className="bgchk-src">Sourced from the CMS National Provider Directory</div>
        </div>
        <div className="mod-visual compact">
          <div className="bgchk-head">
            <span className="bgchk-title">Services, insurance &amp; network</span>
          </div>
          <div className="safety-row"><span className="cv-check"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg></span><span>Medicare acceptance, confirmed directly by CMS</span></div>
          <div className="safety-row"><span className="cv-check"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg></span><span>Locations and contact details, verified not guessed</span></div>
          <div className="safety-row"><span className="cv-check"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg></span><span>Hospital affiliations and practice group listed explicitly</span></div>
        </div>
      </div>
    </div>
  </div>
</section>

<section>
  <div className="wrap">
    <div className="cta-band reveal">
      <h2>See the platform on your own network.</h2>
      <p>Bring a real panel, and we'll show you what's flagged, what's stale, and what's actually good.</p>
      <button className="btn btn-white btn-lg" onClick={() => (window.location.href = "/about#contact")}>Book a Demo</button>
    </div>
  </div>
</section>


      <SiteFooter />
    </>
  );
}